import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Check,
  CircleAlert,
  Download,
  File as FileIcon,
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Loader2,
  Mic,
  Paperclip,
  Pause,
  Play,
  Presentation,
  Trash2,
  X,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { ApiError, api, uploadFile, type Attachment, type FilePolicy, type UploadedFile } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatBytes, formatClock, formatDateTime } from '../lib/format';
import { Button, useToast } from './ui';

/**
 * Files and voice notes on the client's tickets, answers and change requests —
 * the admin's composer, recorder and player, copied (`/files/*` here is the
 * portal's own upload route).
 *
 * A file uploads the moment it is picked (or a voice note the moment it is
 * stopped), so the progress shows while the person is still typing; the post
 * then attaches the finished uploads by id. Pictures and PDFs open in a new tab,
 * everything else downloads, and a voice note plays in place — and every file, a
 * picture included, has its own Download.
 */

// ---------------------------------------------------------------------------
// Kinds of file
// ---------------------------------------------------------------------------

const isImage = (mime: string) => /^image\/(png|jpeg|gif|webp)$/.test(mime);
const isAudio = (mime: string) => mime.startsWith('audio/');
const isPdf = (mime: string) => mime === 'application/pdf';
const isVideo = (mime: string) => mime.startsWith('video/');
/** The videos a browser's own player takes; the rest (avi, mkv, wmv…) are downloads. */
const playsInPage = (mime: string) => /^video\/(mp4|x-m4v|quicktime|webm|ogg)$/.test(mime);

function iconFor(mime: string) {
  if (isImage(mime)) return FileImage;
  if (isAudio(mime)) return FileAudio;
  if (isVideo(mime)) return FileVideo;
  if (mime.includes('sheet') || mime.includes('excel') || mime === 'text/csv') return FileSpreadsheet;
  if (mime.includes('presentation') || mime.includes('powerpoint')) return Presentation;
  if (mime.includes('zip')) return FileArchive;
  if (isPdf(mime) || mime.includes('word') || mime === 'text/plain') return FileText;
  return FileIcon;
}

/** What the recorder asks the browser for, best first: Chrome and Firefox take webm, Safari mp4. */
const RECORDING_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot).toLowerCase() : '';
}

function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** `voice-note-20260930-143205` — the recording's own time, as the person saw it. */
function voiceNoteName(type: string): string {
  const d = new Date();
  const two = (n: number) => String(n).padStart(2, '0');
  const stamp = `${d.getFullYear()}${two(d.getMonth() + 1)}${two(d.getDate())}-${two(d.getHours())}${two(d.getMinutes())}${two(d.getSeconds())}`;
  const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
  return `voice-note-${stamp}.${ext}`;
}

export function useFilePolicy(): FilePolicy | undefined {
  const { data } = useQuery({
    queryKey: ['files', 'policy'],
    queryFn: () => api.get<FilePolicy>('/files/policy'),
    staleTime: 5 * 60_000,
  });
  return data;
}

// ---------------------------------------------------------------------------
// Uploads waiting to be posted
// ---------------------------------------------------------------------------

export interface PendingUpload {
  key: string;
  name: string;
  size: number;
  mimeType: string;
  kind: 'FILE' | 'VOICE_NOTE';
  durationMs?: number;
  /** A local copy — a picture or a recording shows before its upload finishes. */
  previewUrl?: string;
  progress: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
  file?: UploadedFile;
}

export type Uploads = ReturnType<typeof useUploads>;

export function useUploads() {
  const { t } = useI18n();
  const toast = useToast();
  const policy = useFilePolicy();
  const [items, setItems] = useState<PendingUpload[]>([]);
  const [dragging, setDragging] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const controllers = useRef(new Map<string, AbortController>());
  const dragDepth = useRef(0);

  const patch = useCallback(
    (key: string, change: Partial<PendingUpload>) =>
      setItems((prev) => prev.map((item) => (item.key === key ? { ...item, ...change } : item))),
    [],
  );

  const describe = useCallback(
    (e: unknown): string => {
      if (e instanceof ApiError) {
        if (e.code === 'FILE_TOO_LARGE') {
          const details = e.details as { maxMb?: number; video?: boolean } | undefined;
          const mb = details?.maxMb ?? policy?.maxUploadMb;
          if (!mb) return e.message;
          return details?.video ? t('attachments.tooLargeVideo', { mb }) : t('attachments.tooLarge', { mb });
        }
        if (e.code === 'FILE_TYPE_NOT_ALLOWED') return t('attachments.typeNotAllowed');
        if (['FILE_STORAGE_UNREACHABLE', 'FILE_STORAGE_REFUSED', 'FILE_STORAGE_UNAVAILABLE'].includes(e.code)) {
          return t('attachments.storageUnreachable');
        }
        if (e.code === 'FILE_NOT_UPLOADED') return t('attachments.notArrived');
        return e.message;
      }
      return t('attachments.failed');
    },
    [policy, t],
  );

  const start = useCallback(
    (blob: Blob, name: string, kind: PendingUpload['kind'], durationMs?: number) => {
      const key = newKey();
      const mimeType = blob.type.split(';')[0].trim().toLowerCase();
      // A video has its own limit. A webm or an ogg may be either: the browser's type says which.
      const video = isVideo(mimeType) || (policy?.videoExtensions ?? []).includes(extensionOf(name));
      const maxMb = policy ? (video ? (policy.maxVideoMb ?? policy.maxUploadMb) : policy.maxUploadMb) : null;
      const item: PendingUpload = {
        key,
        name,
        size: blob.size,
        mimeType,
        kind,
        durationMs,
        previewUrl: isImage(mimeType) || isAudio(mimeType) ? URL.createObjectURL(blob) : undefined,
        progress: 0,
        status: 'uploading',
      };

      // The server checks again; this only saves sending what it would refuse.
      const problem =
        policy && !policy.extensions.includes(extensionOf(name))
          ? t('attachments.typeNotAllowed')
          : maxMb !== null && blob.size > maxMb * 1024 * 1024
            ? t(video ? 'attachments.tooLargeVideo' : 'attachments.tooLarge', { mb: maxMb })
            : null;
      if (problem) {
        setItems((prev) => [...prev, { ...item, status: 'error', error: problem }]);
        return;
      }

      setItems((prev) => [...prev, item]);
      const controller = new AbortController();
      controllers.current.set(key, controller);
      uploadFile(blob, name, {
        kind,
        durationMs,
        signal: controller.signal,
        onProgress: (fraction) => patch(key, { progress: fraction }),
      })
        .then((file) => patch(key, { status: 'done', progress: 1, file }))
        .catch((e) => {
          if (!controller.signal.aborted) patch(key, { status: 'error', error: describe(e) });
        })
        .finally(() => controllers.current.delete(key));
    },
    [describe, patch, policy, t],
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      const max = policy?.maxFilesPerPost ?? 20;
      const room = max - itemsRef.current.filter((i) => i.status !== 'error').length;
      if (list.length > room) toast.error(t('attachments.tooMany', { n: max }));
      for (const file of list.slice(0, Math.max(0, room))) start(file, file.name, 'FILE');
    },
    [policy, start, t, toast],
  );

  const addRecording = useCallback(
    (blob: Blob, durationMs: number) => start(blob, voiceNoteName(blob.type), 'VOICE_NOTE', durationMs),
    [start],
  );

  const forget = (item: PendingUpload) => {
    controllers.current.get(item.key)?.abort();
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
  };

  const remove = useCallback((key: string) => {
    const item = itemsRef.current.find((i) => i.key === key);
    if (item) forget(item);
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  /** After a successful post: everything went with it. */
  const clear = useCallback(() => {
    itemsRef.current.forEach(forget);
    setItems([]);
  }, []);

  // Leaving the page stops what is still uploading and lets the local copies go.
  useEffect(() => () => itemsRef.current.forEach(forget), []);

  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
  const dropZone = {
    onDragEnter: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    },
    onDragOver: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    },
    onDragLeave: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    },
    onDrop: (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      addFiles(e.dataTransfer.files);
    },
  };

  /** A screenshot pasted into the text box becomes an attachment; pasted text stays text. */
  const onPaste = (e: ClipboardEvent) => {
    const files = Array.from(e.clipboardData?.files ?? []);
    if (files.length === 0) return;
    e.preventDefault();
    addFiles(files);
  };

  const fileIds = useMemo(
    () => items.filter((i) => i.status === 'done' && i.file).map((i) => i.file!.id),
    [items],
  );

  return {
    items,
    policy,
    fileIds,
    /** Something is still on its way up — a post now would leave it behind. */
    busy: items.some((i) => i.status === 'uploading'),
    addFiles,
    addRecording,
    remove,
    clear,
    dropZone,
    dragging,
    onPaste,
  };
}

// ---------------------------------------------------------------------------
// Picking, recording and the tray of what is waiting
// ---------------------------------------------------------------------------

/**
 * The "Attach files" and "Voice note" buttons, the recorder while it runs, and
 * what is waiting to go. `actions` sits at the end of the row — a reply's
 * submit button, the internal-note switch.
 */
export function AttachmentComposer({
  uploads,
  actions,
  className,
}: {
  uploads: Uploads;
  actions?: ReactNode;
  className?: string;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [recording, setRecording] = useState(false);

  return (
    <div className={clsx('space-y-2', className)}>
      <PendingTray uploads={uploads} />

      <div className="flex flex-wrap items-center gap-2">
        {recording ? (
          <VoiceRecorder
            maxSeconds={uploads.policy?.maxVoiceNoteSeconds ?? 600}
            onDone={(blob, ms) => {
              setRecording(false);
              uploads.addRecording(blob, ms);
            }}
            onCancel={() => setRecording(false)}
            onError={(message) => {
              setRecording(false);
              toast.error(message);
            }}
          />
        ) : (
          <>
            <Button
              size="sm"
              variant="ghost"
              icon={<Paperclip className="h-4 w-4" />}
              onClick={() => input.current?.click()}
            >
              {t('attachments.attachFiles')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              icon={<Mic className="h-4 w-4" />}
              title={t('attachments.recordVoiceHint')}
              onClick={() => setRecording(true)}
            >
              {t('attachments.recordVoice')}
            </Button>
            <input
              ref={input}
              type="file"
              multiple
              hidden
              accept={uploads.policy?.extensions.join(',')}
              onChange={(e) => {
                if (e.target.files) uploads.addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            {actions && <div className="ms-auto flex flex-wrap items-center gap-3">{actions}</div>}
          </>
        )}
      </div>
    </div>
  );
}

/** Where files can be dropped: the whole reply box, not just its buttons. */
export function DropZone({
  uploads,
  children,
  className,
}: {
  uploads: Uploads;
  children: ReactNode;
  className?: string;
}) {
  const { t } = useI18n();
  return (
    <div {...uploads.dropZone} className={clsx('relative', className)}>
      {children}
      {uploads.dragging && (
        <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center rounded-md border-2 border-dashed border-primary bg-[var(--surface-raised)] text-body-sm font-medium text-primary-600 dark:text-primary-400">
          {t('attachments.dropHere')}
        </div>
      )}
    </div>
  );
}

/**
 * The attachments block of a form page — a create form's files and voice notes.
 * Not a `Field`: that is a `<label>`, and a label around buttons clicks the
 * first of them whenever its text is clicked.
 */
export function AttachmentsField({ uploads }: { uploads: Uploads }) {
  const { t } = useI18n();
  return (
    <div>
      <span className="mb-1.5 block text-body-sm font-medium text-[var(--text-secondary)]">{t('attachments.title')}</span>
      <DropZone uploads={uploads} className="rounded-md border border-dashed hairline p-3">
        <AttachmentComposer uploads={uploads} />
      </DropZone>
      {uploads.policy && (
        <span className="mt-1 block text-caption text-faint">
          {t('attachments.hint', {
            mb: uploads.policy.maxUploadMb,
            videoMb: uploads.policy.maxVideoMb ?? uploads.policy.maxUploadMb,
          })}
        </span>
      )}
    </div>
  );
}

function PendingTray({ uploads }: { uploads: Uploads }) {
  const { t, locale } = useI18n();
  if (uploads.items.length === 0) return null;

  const pictures = uploads.items.filter((i) => i.status !== 'error' && isImage(i.mimeType));
  const rest = uploads.items.filter((i) => i.status === 'error' || !isImage(i.mimeType));

  return (
    <div className="space-y-2">
      {pictures.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {pictures.map((item) => (
            <li key={item.key} className="relative h-16 w-16 overflow-hidden rounded-md border hairline">
              <img src={item.previewUrl} alt={item.name} className="h-full w-full object-cover" />
              {item.status === 'uploading' && <UploadVeil progress={item.progress} />}
              <RemoveButton label={t('attachments.remove')} onClick={() => uploads.remove(item.key)} floating />
            </li>
          ))}
        </ul>
      )}
      {rest.length > 0 && (
        <ul className="space-y-1.5">
          {rest.map((item) => {
            const Icon = iconFor(item.mimeType);
            return (
              <li
                key={item.key}
                className={clsx(
                  'flex items-center gap-2.5 rounded-md border px-2.5 py-1.5',
                  item.status === 'error' ? 'border-error/30 bg-error/6' : 'hairline bg-[var(--surface-sunken)]',
                )}
              >
                {item.status === 'error' ? (
                  <CircleAlert className="h-4 w-4 shrink-0 text-error-fg dark:text-error-dark" aria-hidden />
                ) : item.kind === 'VOICE_NOTE' && item.previewUrl ? null : (
                  <Icon className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
                )}

                {item.status !== 'error' && item.kind === 'VOICE_NOTE' && item.previewUrl ? (
                  <VoiceNotePlayer src={item.previewUrl} durationMs={item.durationMs} className="min-w-0 flex-1" />
                ) : (
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-body-sm text-[var(--text-primary)]">
                      <bdi>{item.name}</bdi>
                    </p>
                    {item.status === 'error' ? (
                      <p className="text-caption text-error-fg dark:text-error-dark">{item.error}</p>
                    ) : (
                      <p className="text-caption text-muted">
                        {formatBytes(item.size, locale)}
                        {item.status === 'uploading' && ` · ${t('attachments.uploading')}`}
                      </p>
                    )}
                    {item.status === 'uploading' && <ProgressLine progress={item.progress} />}
                  </div>
                )}

                {item.status === 'uploading' && item.kind === 'VOICE_NOTE' && (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted" aria-label={t('attachments.uploading')} />
                )}
                <RemoveButton label={t('attachments.remove')} onClick={() => uploads.remove(item.key)} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function UploadVeil({ progress }: { progress: number }) {
  return (
    <span className="absolute inset-0 grid place-items-center bg-black/40">
      <span className="font-mono text-caption font-medium text-white tabular" dir="ltr">
        {Math.round(progress * 100)}%
      </span>
    </span>
  );
}

function ProgressLine({ progress }: { progress: number }) {
  return (
    <span className="mt-1 block h-1 overflow-hidden rounded-full bg-[var(--line)]">
      <span
        className="block h-full rounded-full bg-primary transition-[width] duration-fast"
        style={{ width: `${Math.round(progress * 100)}%` }}
      />
    </span>
  );
}

function RemoveButton({ label, onClick, floating = false }: { label: string; onClick: () => void; floating?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={clsx(
        'grid shrink-0 place-items-center rounded-full transition-colors duration-fast',
        floating
          ? 'absolute end-1 top-1 h-5 w-5 bg-black/60 text-white hover:bg-black/80'
          : 'h-7 w-7 text-muted hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)]',
      )}
    >
      <X className={floating ? 'h-3 w-3' : 'h-4 w-4'} aria-hidden />
    </button>
  );
}

// ---------------------------------------------------------------------------
// The recorder
// ---------------------------------------------------------------------------

/**
 * Records one voice note. Press "Voice note" again for the next one — a post
 * can carry as many as it likes. Stops by itself at the policy's limit.
 */
function VoiceRecorder({
  maxSeconds,
  onDone,
  onCancel,
  onError,
}: {
  maxSeconds: number;
  onDone: (blob: Blob, durationMs: number) => void;
  onCancel: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useI18n();
  const [live, setLive] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);
  const recorder = useRef<MediaRecorder | null>(null);
  const keep = useRef(false);
  const startedAt = useRef(0);
  // The latest callbacks, whatever render the recorder started in.
  const callbacks = useRef({ onDone, onCancel, onError });
  callbacks.current = { onDone, onCancel, onError };

  const stop = useCallback((keepIt: boolean) => {
    keep.current = keepIt;
    const r = recorder.current;
    if (r && r.state !== 'inactive') r.stop();
    else if (!keepIt) callbacks.current.onCancel();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | null = null;
    let context: AudioContext | null = null;
    let frame = 0;
    let timer = 0;
    const chunks: Blob[] = [];

    void (async () => {
      // Let a StrictMode double mount settle before asking for the microphone.
      await Promise.resolve();
      if (cancelled) return;
      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        callbacks.current.onError(t('attachments.micUnavailable'));
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        const name = (e as DOMException)?.name;
        if (!cancelled) {
          callbacks.current.onError(
            name === 'NotAllowedError' || name === 'SecurityError'
              ? t('attachments.micDenied')
              : t('attachments.micUnavailable'),
          );
        }
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const mimeType = RECORDING_TYPES.find((type) => MediaRecorder.isTypeSupported?.(type));
      let r: MediaRecorder;
      try {
        r = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      } catch {
        stream.getTracks().forEach((track) => track.stop());
        callbacks.current.onError(t('attachments.micUnavailable'));
        return;
      }
      recorder.current = r;
      r.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      r.onstop = () => {
        const duration = performance.now() - startedAt.current;
        stream?.getTracks().forEach((track) => track.stop());
        if (keep.current && chunks.length > 0) {
          callbacks.current.onDone(new Blob(chunks, { type: r.mimeType || mimeType || 'audio/webm' }), duration);
        } else {
          callbacks.current.onCancel();
        }
      };
      r.start(250);
      startedAt.current = performance.now();
      setLive(true);
      timer = window.setInterval(() => {
        const ms = performance.now() - startedAt.current;
        setElapsedMs(ms);
        if (ms >= maxSeconds * 1000) stop(true);
      }, 200);

      // A level meter, so it is plain the microphone is hearing something.
      try {
        context = new AudioContext();
        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        context.createMediaStreamSource(stream).connect(analyser);
        const samples = new Uint8Array(analyser.fftSize);
        const tick = () => {
          analyser.getByteTimeDomainData(samples);
          let sum = 0;
          for (const v of samples) sum += ((v - 128) / 128) ** 2;
          setLevel(Math.min(1, Math.sqrt(sum / samples.length) * 4));
          frame = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        /* the meter is a nicety */
      }
    })();

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      cancelAnimationFrame(frame);
      // Leaving mid-recording throws the recording away, quietly.
      const r = recorder.current;
      if (r && r.state !== 'inactive') {
        r.onstop = null;
        r.stop();
      }
      stream?.getTracks().forEach((track) => track.stop());
      void context?.close().catch(() => undefined);
    };
    // The recorder runs once per mount; its callbacks are read through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 rounded-md border border-error/30 bg-error/6 px-3 py-1.5">
      <span className={clsx('h-2.5 w-2.5 shrink-0 rounded-full bg-error', live && 'animate-pulse')} aria-hidden />
      <span className="text-body-sm font-medium text-error-fg dark:text-error-dark">
        {live ? t('attachments.recording') : t('attachments.startingMic')}
      </span>
      <span
        className="font-mono text-body-sm text-[var(--text-primary)] tabular"
        dir="ltr"
        title={t('attachments.maxLength', { min: Math.round(maxSeconds / 60) })}
      >
        {formatClock(elapsedMs / 1000)}
      </span>
      <span className="flex h-4 items-end gap-0.5" aria-hidden>
        {[0.1, 0.3, 0.5, 0.7, 0.9].map((threshold, i) => (
          <span
            key={threshold}
            className={clsx('w-1 rounded-full transition-colors duration-fast', level > threshold ? 'bg-error' : 'bg-error/25')}
            style={{ height: `${40 + i * 15}%` }}
          />
        ))}
      </span>
      <span className="ms-auto flex gap-1.5">
        <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={() => stop(false)}>
          {t('attachments.discardRecording')}
        </Button>
        <Button size="sm" variant="primary" icon={<Check className="h-4 w-4" />} disabled={!live} onClick={() => stop(true)}>
          {t('attachments.stopRecording')}
        </Button>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Playing a voice note
// ---------------------------------------------------------------------------

/** One voice note plays at a time: starting another pauses the last. */
let playingNow: HTMLAudioElement | null = null;

/**
 * Play, a seek bar, the time and a speed switch. Laid out left to right in
 * either language: media controls follow the tape, not the reading direction.
 */
export function VoiceNotePlayer({
  src,
  durationMs,
  className,
}: {
  src: string;
  durationMs?: number | null;
  className?: string;
}) {
  const { t } = useI18n();
  const audio = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [length, setLength] = useState<number | null>(durationMs ? durationMs / 1000 : null);
  const [rate, setRate] = useState(1);
  const [failed, setFailed] = useState(false);
  // Chrome's own webm recordings carry no length until read through; reading to
  // the end once, silently, makes the browser work it out.
  const probing = useRef(false);

  const duration = length ?? 0;

  const learnLength = () => {
    const el = audio.current;
    if (!el) return;
    if (Number.isFinite(el.duration) && el.duration > 0) {
      setLength(el.duration);
      if (probing.current) {
        probing.current = false;
        el.currentTime = 0;
      }
    } else if (!durationMs && !probing.current) {
      probing.current = true;
      el.currentTime = 1e101;
    }
  };

  const toggle = async () => {
    const el = audio.current;
    if (!el) return;
    if (!el.paused) {
      el.pause();
      return;
    }
    if (playingNow && playingNow !== el) playingNow.pause();
    playingNow = el;
    try {
      el.playbackRate = rate;
      await el.play();
      setFailed(false);
    } catch {
      setFailed(true);
    }
  };

  const seekTo = (seconds: number) => {
    const el = audio.current;
    if (!el || !duration) return;
    el.currentTime = Math.min(duration, Math.max(0, seconds));
    setPosition(el.currentTime);
  };

  const onTrackClick = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seekTo(((e.clientX - rect.left) / rect.width) * duration);
  };

  const onTrackKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') seekTo(position + 5);
    else if (e.key === 'ArrowLeft') seekTo(position - 5);
    else return;
    e.preventDefault();
  };

  const nextRate = () => {
    const next = rate === 1 ? 1.5 : rate === 1.5 ? 2 : 1;
    setRate(next);
    if (audio.current) audio.current.playbackRate = next;
  };

  const shown = playing || position > 0 ? position : duration;
  const percent = duration ? Math.min(100, (position / duration) * 100) : 0;

  return (
    <div className={clsx('min-w-0', className)}>
      <div className="flex items-center gap-2.5 rounded-full bg-[var(--surface-sunken)] py-1 pe-3 ps-1" dir="ltr">
        <button
          type="button"
          onClick={() => void toggle()}
          aria-label={playing ? t('attachments.pause') : t('attachments.play')}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-white transition-colors duration-fast hover:bg-primary-600"
        >
          {playing ? <Pause className="h-4 w-4" aria-hidden /> : <Play className="ms-0.5 h-4 w-4" aria-hidden />}
        </button>
        <div
          role="slider"
          tabIndex={0}
          aria-label={t('attachments.voiceNote')}
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(position)}
          aria-valuetext={`${formatClock(position)} / ${formatClock(duration)}`}
          onClick={onTrackClick}
          onKeyDown={onTrackKey}
          className="relative h-1.5 min-w-[64px] flex-1 cursor-pointer rounded-full bg-[var(--line)]"
        >
          <span className="absolute inset-y-0 left-0 rounded-full bg-primary" style={{ width: `${percent}%` }} />
        </div>
        <span className="shrink-0 font-mono text-caption text-muted tabular">{formatClock(shown)}</span>
        <button
          type="button"
          onClick={nextRate}
          aria-label={t('attachments.speed')}
          title={t('attachments.speed')}
          className="shrink-0 rounded-xs px-1 font-mono text-caption text-muted transition-colors duration-fast hover:text-[var(--text-primary)]"
        >
          {rate}×
        </button>
        <audio
          ref={audio}
          src={src}
          preload="metadata"
          onLoadedMetadata={learnLength}
          onDurationChange={learnLength}
          onTimeUpdate={() => {
            if (!probing.current && audio.current) setPosition(audio.current.currentTime);
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setPosition(0);
          }}
          onError={() => setFailed(true)}
        />
      </div>
      {failed && <p className="mt-1 text-caption text-error-fg dark:text-error-dark">{t('attachments.playFailed')}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Files already posted
// ---------------------------------------------------------------------------

/**
 * Pictures as thumbnails, voice notes as players, documents as rows, each with
 * its Download (a picture's on its corner). Removing one asks first, in place —
 * no dialog.
 */
export function AttachmentList({
  attachments,
  showUploader = false,
  canRemove,
  onRemove,
  removing,
  className,
}: {
  attachments: Attachment[] | undefined;
  /** For a record's own files: who added each one, and when. A reply's files are plainly the author's. */
  showUploader?: boolean;
  canRemove?: (attachment: Attachment) => boolean;
  onRemove?: (attachment: Attachment) => void;
  /** The attachment being removed right now. */
  removing?: string | null;
  className?: string;
}) {
  const { t, locale, localized } = useI18n();
  const [confirming, setConfirming] = useState<Attachment | null>(null);
  if (!attachments?.length) return null;

  const pictures = attachments.filter((a) => isImage(a.file.mimeType));
  const videos = attachments.filter((a) => playsInPage(a.file.mimeType));
  const recordings = attachments.filter((a) => isAudio(a.file.mimeType));
  const documents = attachments.filter(
    (a) => !isImage(a.file.mimeType) && !isAudio(a.file.mimeType) && !playsInPage(a.file.mimeType),
  );
  const removable = (a: Attachment) => !!onRemove && (canRemove?.(a) ?? false);
  const byLine = (a: Attachment) =>
    `${t('attachments.addedBy', { name: localized(a.file.uploadedBy) })} · ${formatDateTime(a.createdAt, locale)}`;
  const askToRemove = (a: Attachment) => (
    <RemoveButton label={t('attachments.remove')} onClick={() => setConfirming(a)} />
  );

  return (
    <div className={clsx('space-y-2', className)}>
      {pictures.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {pictures.map((a) => (
            <li key={a.id} className="relative">
              <a
                href={a.file.url}
                target="_blank"
                rel="noopener noreferrer"
                title={showUploader ? `${a.file.originalName} — ${byLine(a)}` : a.file.originalName}
                className="block h-24 w-24 overflow-hidden rounded-md border hairline transition-colors duration-fast hover:border-primary/60"
              >
                <img src={a.file.url} alt={a.file.originalName} loading="lazy" className="h-full w-full object-cover" />
              </a>
              {/* Opening a picture only shows it, so it has its own Download, as every other file has. */}
              <a
                href={a.file.downloadUrl}
                aria-label={t('attachments.download')}
                title={t('attachments.download')}
                className="absolute bottom-1 end-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white transition-colors duration-fast hover:bg-black/80"
              >
                <Download className="h-3.5 w-3.5" aria-hidden />
              </a>
              {removable(a) && (
                <RemoveButton label={t('attachments.remove')} onClick={() => setConfirming(a)} floating />
              )}
            </li>
          ))}
        </ul>
      )}

      {videos.map((a) => (
        <VideoAttachment
          key={a.id}
          attachment={a}
          caption={showUploader ? byLine(a) : null}
          remove={removable(a) ? askToRemove(a) : null}
        />
      ))}

      {recordings.map((a) => (
        <div key={a.id}>
          <div className="flex items-center gap-1.5">
            <VoiceNotePlayer src={a.file.url} durationMs={a.file.durationMs} className="min-w-0 max-w-sm flex-1" />
            <a
              href={a.file.downloadUrl}
              aria-label={t('attachments.download')}
              title={t('attachments.download')}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted transition-colors duration-fast hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)]"
            >
              <Download className="h-4 w-4" aria-hidden />
            </a>
            {removable(a) && askToRemove(a)}
          </div>
          {/* A recording made here needs no name; an audio file keeps its own. */}
          {(a.file.kind !== 'VOICE_NOTE' || showUploader) && (
            <p className="mt-0.5 ps-1 text-caption text-muted">
              {a.file.kind === 'VOICE_NOTE' ? t('attachments.voiceNote') : <bdi>{a.file.originalName}</bdi>}
              {showUploader && ` · ${byLine(a)}`}
            </p>
          )}
        </div>
      ))}

      {documents.length > 0 && (
        <ul className="space-y-1.5">
          {documents.map((a) => {
            const Icon = iconFor(a.file.mimeType);
            const opensInTab = isPdf(a.file.mimeType);
            return (
              <li key={a.id} className="flex items-center gap-3 rounded-md border hairline px-3 py-2">
                <Icon className="h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
                <div className="min-w-0 flex-1">
                  <a
                    href={opensInTab ? a.file.url : a.file.downloadUrl}
                    target={opensInTab ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className="block truncate text-body-sm font-medium text-[var(--text-primary)] hover:text-primary-600 dark:hover:text-primary-400 hover:underline"
                    title={opensInTab ? t('attachments.open') : t('attachments.download')}
                  >
                    <bdi>{a.file.originalName}</bdi>
                  </a>
                  <p className="text-caption text-muted">
                    {formatBytes(a.file.sizeBytes, locale)}
                    {showUploader && ` · ${byLine(a)}`}
                  </p>
                </div>
                <a
                  href={a.file.downloadUrl}
                  aria-label={t('attachments.download')}
                  title={t('attachments.download')}
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted transition-colors duration-fast hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)]"
                >
                  <Download className="h-4 w-4" aria-hidden />
                </a>
                {removable(a) && askToRemove(a)}
              </li>
            );
          })}
        </ul>
      )}

      {confirming && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-warning/35 bg-warning/10 px-3 py-2">
          <span className="min-w-0 flex-1 text-body-sm text-warning-fg dark:text-warning-dark">
            {t('attachments.removeConfirm')}{' '}
            <strong className="font-medium">
              <bdi>{confirming.file.originalName}</bdi>
            </strong>
          </span>
          <Button size="sm" onClick={() => setConfirming(null)}>
            {t('attachments.keep')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            loading={removing === confirming.id}
            onClick={() => {
              onRemove?.(confirming);
              setConfirming(null);
            }}
          >
            {t('attachments.remove')}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * A video plays in place, in the browser's own player. One the browser cannot
 * decode — an unusual codec inside an mp4 or a .mov — says so and keeps its
 * download.
 */
function VideoAttachment({
  attachment,
  caption,
  remove,
}: {
  attachment: Attachment;
  /** Who added it and when, for a record's own files. */
  caption: string | null;
  remove: ReactNode;
}) {
  const { t, locale } = useI18n();
  const [failed, setFailed] = useState(false);
  const { file } = attachment;

  return (
    <div className="max-w-md">
      {!failed && (
        <video
          src={file.url}
          controls
          preload="metadata"
          playsInline
          aria-label={file.originalName}
          onError={() => setFailed(true)}
          className="block aspect-video w-full rounded-md border hairline bg-black object-contain"
        />
      )}
      <div className={clsx('flex items-center gap-2', !failed && 'mt-1.5')}>
        <FileVideo className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="truncate text-body-sm text-[var(--text-primary)]">
            <bdi>{file.originalName}</bdi>
          </p>
          <p className="text-caption text-muted">
            {formatBytes(file.sizeBytes, locale)}
            {caption && ` · ${caption}`}
          </p>
        </div>
        <a
          href={file.downloadUrl}
          aria-label={t('attachments.download')}
          title={t('attachments.download')}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted transition-colors duration-fast hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)]"
        >
          <Download className="h-4 w-4" aria-hidden />
        </a>
        {remove}
      </div>
      {failed && <p className="mt-1 text-caption text-error-fg dark:text-error-dark">{t('attachments.videoUnplayable')}</p>}
    </div>
  );
}
